import type {
  Player,
  PlayerProfile,
  CreatePlayerRequest,
  UpdatePlayerRequest,
} from '@swiss-magic/shared-types';
import { get, post, patch, del } from './core';

export function getPlayers(
  tournamentId: string,
  view: 'default' | 'blocked' = 'default',
): Promise<{ players: Player[]; lastUpdatedAt: string }> {
  const url =
    view === 'blocked'
      ? `/tournaments/${tournamentId}/players?view=blocked`
      : `/tournaments/${tournamentId}/players`;
  return get(url);
}

export function getPlayersLastUpdatedAt(
  tournamentId: string,
): Promise<{ at: string }> {
  return get(`/tournaments/${tournamentId}/players/last-updated-at`);
}

export function getBlockedPlayersCount(tournamentId: string): Promise<{ count: number }> {
  return get(`/tournaments/${tournamentId}/players/blocked-count`);
}

export function addPlayer(
  tournamentId: string,
  data: CreatePlayerRequest,
  force?: boolean,
): Promise<Player> {
  const url = force
    ? `/tournaments/${tournamentId}/players?force=true`
    : `/tournaments/${tournamentId}/players`;
  return post(url, data);
}

export function updatePlayer(
  tournamentId: string,
  playerId: string,
  data: UpdatePlayerRequest,
): Promise<Player> {
  return patch(`/tournaments/${tournamentId}/players/${playerId}`, data);
}

// Story 100 — secure a pending/waitlisted player's spot.
export function securePlayerSpot(
  tournamentId: string,
  playerId: string,
): Promise<Player> {
  return post(`/tournaments/${tournamentId}/players/${playerId}/secure-spot`);
}

export function removePlayer(tournamentId: string, playerId: string): Promise<void> {
  return del(`/tournaments/${tournamentId}/players/${playerId}`);
}

export function withdrawPlayer(tournamentId: string, playerId: string): Promise<Player> {
  return post(`/tournaments/${tournamentId}/players/${playerId}/withdraw`);
}

export function refreshPlayerFromProfile(
  tournamentId: string,
  playerId: string,
): Promise<Player> {
  return post(`/tournaments/${tournamentId}/players/${playerId}/refresh-from-profile`);
}

export function refreshAllPlayersFromProfiles(
  tournamentId: string,
): Promise<{ ok: boolean }> {
  return post(`/tournaments/${tournamentId}/players/refresh-from-profiles`);
}

export function rejectPlayer(
  tournamentId: string,
  playerId: string,
  reason: string | null,
  block = false,
): Promise<Player> {
  return post(`/tournaments/${tournamentId}/players/${playerId}/reject`, {
    reason,
    block,
  });
}

export function unblockPlayer(
  tournamentId: string,
  playerId: string,
): Promise<Player> {
  return post(`/tournaments/${tournamentId}/players/${playerId}/unblock`);
}

export function getConnectedPlayerProfile(
  tournamentId: string,
  playerId: string,
): Promise<PlayerProfile> {
  return get(`/tournaments/${tournamentId}/players/${playerId}/profile`);
}
